/* This component is to render pieChart it expects the data in the format:
const pieChartData = [
  { name: 'Jan 1', electricMeterValue: 400 },
  { name: 'Feb 1', electricMeterValue: 300 },
];
It will show the piechart only when the data is passed in the above way */

import { useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { LegendIcon } from '../styles';
import { chartColors } from './chartHelpers';
import { ChartTooltipStyles, StyledLegendWrapper } from './styles';
import EmptyState from '../DesignSystem/EmptyState';
import { useTranslation } from 'react-i18next';

const getCell = (pieChartData: any, activeIndex: number) => {
    return pieChartData?.map((entry: any, index: any) => (
        <Cell
            key={`cell-${index}`}
            fill={chartColors[index % chartColors.length]}
            stroke={index === activeIndex ? '#4B74BE' : 'none'}
            strokeWidth={4}
        />
    ));
};

const GetCustomisedLegend = ({ payload: legendData }: any) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    return (
        <>
            {legendData.length > 8 && (
                <div className='d-flex justify-content-end'>
                    <i
                        className={`fa ps-2 me-4 fa-angle-${isExpanded ? 'up' : 'down'}`}
                        aria-hidden='true'
                        onClick={() => setIsExpanded(!isExpanded)}
                    />
                </div>
            )}
            <StyledLegendWrapper
                className='d-flex flex-wrap col-12 '
                style={!isExpanded ? { marginTop: 20, height: 100, overflow: 'hidden' } : {}}
            >
                {legendData.map((entry: any, index: number) => (
                    <div className='item d-flex align-items-center col-3'>
                        <LegendIcon color={entry.color} />
                        <p className='m-0' key={`item-${index}`} title={t(entry.value) || ''}>
                            {t(entry.value)}
                        </p>
                    </div>
                ))}
            </StyledLegendWrapper>
        </>
    );
};

const CustomTooltip = ({ active, payload }: any) => {
    const { t } = useTranslation();
    if (active && payload && payload.length) {
        return payload.map((item: any) => (
            <ChartTooltipStyles>
                <div className='label'>{t(item.payload.name)}</div>
                <p> value : {item.value}</p>
            </ChartTooltipStyles>
        ));
    }
    return null;
};

const PieCharts = (props: any) => {
    const {
        containerHeight = 400,
        chartTitle = '',
        pieChartData,
        innerRadius,
        outerRadius
    } = props;

    const [activeIndex, setActiveIndex] = useState(-1);

    const renderCustomizedLabel = ({
        cx,
        cy,
        midAngle,
        innerRadius,
        outerRadius,
        percent
    }: any) => {
        const RADIAN = Math.PI / 180;
        const radius = 40 + innerRadius + (outerRadius - innerRadius);
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        if (percent === 0) return null;
        return (
            <>
                <text x={x} y={y} fill='black' textAnchor={'middle'} dominantBaseline='central'>
                    {`${Math.round(percent * 100) + '%'}`}
                </text>
            </>
        );
    };
    const renderCustomizedLabelLine = (props: any) => {
        return props.value !== 0 ? (
            <path
                stroke={props.stroke}
                d={`M${props.points[0].x},${props.points[0].y}L${props.points[1].x},${props.points[1].y}`}
            />
        ) : (
            <polyline stroke={props.stroke} fill='none' />
        );
    };

    const onPieEnter = (e: any, index: number) => {
        setActiveIndex(index);
    };

    const onPieLeave = () => {
        setActiveIndex(-1);
    };

    if (!pieChartData.length) {
        return <EmptyState />;
    }
    return (
        <ResponsiveContainer width='100%' height={containerHeight}>
            <PieChart
                margin={{
                    top: 15,
                    right: 10,
                    left: 10,
                    bottom: 0
                }}
            >
                <text x={'50%'} y={10} fill='black' textAnchor='middle' dominantBaseline='central'>
                    <tspan fontSize='20'>{chartTitle}</tspan>
                </text>
                <Pie
                    data={pieChartData}
                    dataKey={'value'}
                    label={renderCustomizedLabel}
                    labelLine={renderCustomizedLabelLine}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    minAngle={3}
                    paddingAngle={3}
                    fill={'transparent'}
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                >
                    {getCell(pieChartData, activeIndex)}
                </Pie>
                <Legend content={GetCustomisedLegend} />
                <Tooltip
                    content={<CustomTooltip />}
                    wrapperStyle={{
                        outline: 'none'
                    }}
                />
            </PieChart>
        </ResponsiveContainer>
    );
};

export default PieCharts;
